import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useDictionary, useCertificationText } from "hooks";
import { useRichTextAccessibility } from "hooks/accessibility";
import { saveCertificationText } from "store/actions/tenant";
import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import ConfirmationModal from "components/ConfirmationModal";
import RichText from "@cayuse/shared-react.components.form.inputs.rich-text";
import FormattedDatetime from "@cayuse/shared-react.components.formatted-datetime";
import FormattedRichText from "@cayuse/shared-react.components.formatted-rich-text";

import styles from "./styles.css";

const EditableTextView = props => {
	const {
		text,
		onTextChange,
		editedText,
		isLoading,
		handleOnReset,
		handleOnCancel,
		handleOnSave,
		dictionary,
	} = props;

	const richTextRef = useRef(null);

	useRichTextAccessibility(richTextRef);

	return (
		<div className={styles.certificationEditor} ref={richTextRef}>
			<RichText
				maxLength="5000"
				onChange={onTextChange}
				placeholder={dictionary.notePlaceholder || "Enter the certification text here..."}
				value={editedText}
			/>
			<ButtonGroup className={styles.actionsWrapper}>
				<Button primary disabled={isLoading} onClick={handleOnReset}>
					{dictionary.actions.resetToDefault || "Set to default text"}
				</Button>
				<ButtonGroup className={styles.actionButtonGroup}>
					<Button secondary disabled={isLoading} onClick={handleOnCancel}>
						{dictionary.actions.cancel || "Cancel"}
					</Button>
					<Button
						disabled={text === editedText}
						busy={isLoading}
						onClick={handleOnSave}
					>
						{dictionary.actions.save || "Save"}
					</Button>
				</ButtonGroup>
			</ButtonGroup>
		</div>
	);
};

const ReadonlyTextView = props => {
	const { updateDate, updatedByUserFullName, isLoading, handleOnEdit, editedText, dictionary, headerTitle } = props;

	return (
		<div>
			<div className={styles.certificationHeader}>
				{updateDate ? (
					<div className={styles.certificationHeaderLabel}>
						<span>Updated: </span>
						<FormattedDatetime date={updateDate} />
						<span> by {updatedByUserFullName}</span>
					</div>
				) : <div className={styles.certificationHeaderLabel}/>}
				<ButtonGroup className={styles.buttonGroup}>
					<Button busy={isLoading} onClick={handleOnEdit} aria-label={`Edit ${headerTitle}`}>
						{dictionary.actions.edit || "Edit"}
					</Button>
				</ButtonGroup>
			</div>
			<FormattedRichText className={styles.certificationText} value={editedText} />
		</div>
	);
};

/* todo: scrub for dictionary entries */
const CertificationTextView = ({
	formType,
	dirtyCallback: setDirty,
	headerTitle
}) => {
	const { text, updateDate, updatedByUserFullName } = useCertificationText(formType);
	const [ cancelText, setCancelText ] = useState(text);
	const [ editedText, setEditedText ] = useState(text);
	const [ isEditing, setEditing ] = useState(false);
	const [ isLoading, setIsLoading ] = useState(false);
	const [ isConfirmReset, setConfirmReset ] = useState(false);
	const dictionary = useDictionary("certificationText");

	useEffect(() => {
		setDirty(isNonEmptyString(editedText) && editedText !== cancelText);
	}, [ cancelText, editedText, setDirty ]);

	useEffect(() => {
		setCancelText(text);
		setEditedText(text);
	}, [ text ]);

	const onTextChange = ({ value }) => {
		setEditedText(value);
	};

	const handleOnSave = () => {
		setConfirmReset(false);
		setIsLoading(true);
		saveCertificationText(editedText, formType)
			.finally(() => {
				setIsLoading(false);
				setEditing(false);
			});
		setCancelText(editedText);
	};

	const handleOnReset = () => {
		setConfirmReset(true);
	};

	const handleOnResetConfirmation = () => {
		setIsLoading(true);
		setConfirmReset(false);
		setEditing(false);
		saveCertificationText(null, formType)
			.finally(() => setIsLoading(false));
	};

	const handleOnCancel = () => {
		setEditedText(cancelText);
		setEditing(false);
	};

	const handleOnEdit = () => {
		setEditing(true);
	};

	return (
		<div>
			{isNonEmptyString(text) &&
			<div>
				{isEditing ? (
					<EditableTextView
						text={text}
						onTextChange={onTextChange}
						editedText={editedText}
						isLoading={isLoading}
						handleOnReset={handleOnReset}
						handleOnCancel={handleOnCancel}
						handleOnSave={handleOnSave}
						dictionary={dictionary}
					/>
				) : (
					<ReadonlyTextView
						updateDate={updateDate}
						updatedByUserFullName={updatedByUserFullName}
						isLoading={isLoading}
						handleOnEdit={handleOnEdit}
						editedText={editedText}
						dictionary={dictionary}
						headerTitle={headerTitle}
					/>
				)}
			</div>
			}
			{isConfirmReset && (
				<ConfirmationModal
					cancelButtonLabel={dictionary.modal.confirmReset.cancelButtonLabel}
					confirmButtonLabel={dictionary.modal.confirmReset.confirmButtonLabel}
					icon="file-alt"
					message={dictionary.modal.confirmReset.message}
					onCancel={() => setConfirmReset(false)}
					title={dictionary.modal.confirmReset.title}
					visible={true}
					onConfirm={handleOnResetConfirmation}
				/>
			)}
		</div>
	);
};

CertificationTextView.propTypes = {
	formType: PropTypes.string.isRequired,
	dirtyCallback: PropTypes.func.isRequired
};

export default CertificationTextView;
